import { Card, CardContent } from '@/components/ui/card';
import { Eye, Target, ShieldCheck } from 'lucide-react';
import { useSite } from '@/context/SiteContext';
import { motion } from 'framer-motion';
import RichContent from '@/components/shared/RichContent';

const VisionMissionSection = () => {
    const { settings } = useSite();
    if (!settings) return null;

    const cards = [
        {
            icon: Eye,
            title: 'Our Vision',
            body: settings.vision,
            iconColor: 'text-[hsl(var(--primary))]',
            iconBg: 'bg-[hsl(var(--primary))]/10',
            testId: 'vm-card-vision',
        },
        {
            icon: Target,
            title: 'Our Mission',
            body: settings.mission,
            iconColor: 'text-[hsl(var(--accent))]',
            iconBg: 'bg-[hsl(var(--accent))]/15',
            testId: 'vm-card-mission',
        },
        {
            icon: ShieldCheck,
            title: 'Quality Policy',
            body: settings.quality_policy,
            iconColor: 'text-[hsl(var(--primary))]',
            iconBg: 'bg-[hsl(var(--primary))]/10',
            testId: 'vm-card-quality-policy',
        },
    ];

    // Only render cards that have content — keeps layout clean if one field is empty.
    const visibleCards = cards.filter((c) => (c.body || '').toString().trim().length > 0);
    if (visibleCards.length === 0) return null;

    // Choose grid columns based on visible cards count so cards keep good balance.
    const gridCols =
        visibleCards.length === 3
            ? 'sm:grid-cols-2 lg:grid-cols-3'
            : visibleCards.length === 2
            ? 'sm:grid-cols-2'
            : 'grid-cols-1';

    return (
        <section
            className="section-pad bg-[hsl(var(--secondary))]/60 relative overflow-hidden"
            data-testid="home-vision-mission-section"
        >
            <div className="absolute inset-0 grain-bg opacity-70 pointer-events-none" />
            <div className="container-page relative">
                <div className="text-center max-w-2xl mx-auto mb-10">
                    <div className="flex items-center justify-center gap-3 mb-3">
                        <span className="accent-bar" />
                        <span className="text-[hsl(var(--accent))] font-semibold text-xs uppercase tracking-widest">
                            What Drives Us
                        </span>
                    </div>
                    <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
                        Vision, Mission & Quality Policy
                    </h2>
                </div>
                <div className={`grid ${gridCols} gap-6`}>
                    {visibleCards.map((c, i) => {
                        const Icon = c.icon;
                        return (
                            <motion.div
                                key={c.title}
                                initial={{ opacity: 0, y: 10 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.45, delay: i * 0.08 }}
                                viewport={{ once: true, margin: '-60px' }}
                                data-testid={c.testId}
                            >
                                <Card className="h-full border-border shadow-[0_10px_30px_rgba(15,23,42,0.05)] bg-card">
                                    <CardContent className="p-8">
                                        <div
                                            className={`inline-flex h-12 w-12 items-center justify-center rounded-xl ${c.iconBg} ${c.iconColor} mb-5`}
                                        >
                                            <Icon className="h-6 w-6" />
                                        </div>
                                        <div className="font-display font-semibold text-xl mb-2">
                                            {c.title}
                                        </div>
                                        <RichContent
                                            html={c.body}
                                            className="text-foreground/80 leading-relaxed text-[15px]"
                                        />
                                    </CardContent>
                                </Card>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

export default VisionMissionSection;
